import { useEffect, useMemo, useState } from 'react';
import '../../assets/css/case-property.css';
import caseVerticalIcon from '../../assets/images/case-vertical-icon.svg';
// @ts-ignore
import { useProperty, useGallery, useVirtualTours, RichTextContent } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PhotoLightbox } from './PhotoLightbox';

const PHOTOS_PER_PAGE = 8;
const EXCLUDED_TITLES = new Set(['logo', 'other', 'floorplan']);
type FilterType = 'all' | 'apartment' | 'community';

function matchesFilter(photo: any, filter: FilterType): boolean {
  if (filter === 'all') return true;
  return (photo.title || '').toLowerCase() === filter;
}

/** apartments.com (and similar listing hosts) refuse iframe embeds via X-Frame-Options. */
function isExternalTourLink(url: string): boolean {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    return host === 'apartments.com' || host.endsWith('.apartments.com');
  } catch {
    return false;
  }
}

export function PropertyGallery() {
  const property = useProperty();
  const gallery = useGallery();
  const virtualTours = useVirtualTours();

  const name = property.property_name || '';

  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filtered = useMemo(
    () => gallery
      .filter((p: any) => !EXCLUDED_TITLES.has((p.title || '').toLowerCase().trim()))
      .filter((p: any) => matchesFilter(p, activeFilter)),
    [gallery, activeFilter]
  );

  const displayPhotos = useMemo(
    () => filtered.slice(0, page * PHOTOS_PER_PAGE),
    [filtered, page]
  );

  const hasMore = displayPhotos.length < filtered.length;
  const lightboxPhotos = useMemo(
    () => filtered.map((p: any) => ({
      src: p.image ?? p.original_url ?? '',
      alt: p.alt_text ?? p.title ?? '',
    })).filter((p) => p.src),
    [filtered]
  );

  const getLightboxIndex = (filteredIdx: number) =>
    filtered.slice(0, filteredIdx + 1).filter((p: any) => (p.image ?? p.original_url ?? '')).length - 1;

  const openLightbox = (filteredIdx: number) => {
    setLightboxIndex(Math.max(0, getLightboxIndex(filteredIdx)));
    setLightboxOpen(true);
  };

  const firstTour = virtualTours.length > 0 ? (virtualTours[0] as any) : null;
  const virtualTourUrl = firstTour?.virtual_tour_url
    || (property as any).property_virtual_tour_url
    || (property as any).property_3d_tour_url
    || (property as any).property_self_guided_tour_url
    || '';
  const openTourExternally = Boolean(virtualTourUrl && isExternalTourLink(virtualTourUrl));
  const virtualTourThumbnail = firstTour?.virtual_tour_thumbnail
    || property.property_featured_image
    || (gallery as any[]).find((p) => p.image || p.original_url)?.image
    || (gallery as any[]).find((p) => p.image || p.original_url)?.original_url
    || '';

  useEffect(() => {
    if (!virtualTourUrl || typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('section') !== 'virtual-tours') return;

    window.requestAnimationFrame(() => {
      document.getElementById('virtual-tours')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [virtualTourUrl]);

  return (
    <>
      <section className="prop-al-content-cols prop-al-bg-light">
        <div className="prop-al-container-main">
          <div className="prop-gal-filter-bar">
            <div className="prop-gal-filters">
              <button
                type="button"
                className={`prop-gal-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => { setActiveFilter('all'); setPage(1); }}
              >
                All
              </button>
              <button
                type="button"
                className={`prop-gal-filter-btn ${activeFilter === 'apartment' ? 'active' : ''}`}
                onClick={() => { setActiveFilter('apartment'); setPage(1); }}
              >
                Apartment
              </button>
              <button
                type="button"
                className={`prop-gal-filter-btn ${activeFilter === 'community' ? 'active' : ''}`}
                onClick={() => { setActiveFilter('community'); setPage(1); }}
              >
                Community
              </button>
            </div>
          </div>

          {filtered.length > 0 ? (
            <>
              <div className="prop-gal-grid">
                {displayPhotos.map((photo: any) => {
                  const src = photo.image ?? photo.original_url ?? '';
                  const alt = photo.alt_text ?? photo.title ?? '';
                  if (!src) return null;
                  const filteredIdx = filtered.indexOf(photo);
                  return (
                    <div key={filteredIdx} className="prop-gal-item">
                      <button
                        type="button"
                        className="prop-gal-item-inner"
                        onClick={() => openLightbox(filteredIdx)}
                      >
                        <VibesitesImage url={src} alt={alt} sizes="(min-width: 1400px) 25vw, (min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <div className="prop-gal-load-more">
                  <button
                    type="button"
                    className="prop-btn"
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="prop-gal-empty">No photos match this filter.</p>
          )}
        </div>
      </section>

      <PhotoLightbox
        photos={lightboxPhotos}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {virtualTourUrl && (
        <section id="virtual-tours" className="prop-gal-vt prop-gal-vt-has-overlay">
          <div className="prop-gal-vt-overlay" role="presentation" aria-hidden="true">
            <img src={caseVerticalIcon} alt="" />
          </div>
          <div className="prop-al-container-main">
            <p className="prop-gal-vt-label">Virtual Tour</p>
            <RichTextContent path="propertygallery.virtual_tour_title" label="Virtual Tour Title" defaultValue={contentSchema.tokens['propertygallery.virtual_tour_title'].defaultValue} as="h2" />
            <div className="prop-al-separator" />
            {openTourExternally ? (
              <a
                className="prop-gal-vt-external"
                href={virtualTourUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-vibesite-event="virtual_tour_clicked"
              >
                {virtualTourThumbnail ? (
                  <span className="prop-gal-vt-external-media">
                    <VibesitesImage
                      url={virtualTourThumbnail}
                      alt={`Virtual tour of ${name}`}
                      sizes="(min-width: 1200px) 1100px, 100vw"
                    />
                  </span>
                ) : (
                  <span className="prop-gal-vt-external-fallback" aria-hidden="true" />
                )}
                <span className="prop-btn prop-gal-vt-external-cta">View Virtual Tour</span>
              </a>
            ) : (
              <iframe
                className="prop-gal-vt-iframe"
                src={virtualTourUrl}
                title={`Virtual Tour of ${name}`}
                allowFullScreen
                loading="lazy"
              />
            )}
          </div>
        </section>
      )}
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyGallery',
  displayName: 'Property Gallery Full Page',
  tokens: {
    'propertygallery.virtual_tour_title': {
      label: 'Virtual Tour Section Title',
      type: 'richtext',
      defaultValue: 'Virtual Tour <span data-type="field-token" data-field="property_name"></span>',
    },
  },
};

(PropertyGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyGallery;
